import { Linkedin, Instagram, ShieldCheck } from "lucide-react";
import { BRAND, scrollToId } from "@/lib/site";

const navLinks = [
  { label: "Home", id: "home" },
  { label: "Our Approach", id: "approach" },
  { label: "Services", id: "services" },
  { label: "About", id: "about" },
  { label: "Disclosure", id: "disclosure", href: "/disclosure" },
  { label: "Contact", id: "contact" },
];

const serviceLinks = [
  "Mutual Funds",
  "Insurance",
  "Stock Broking",
  "Fixed Income Products",
  "PMS / AIF",
  "Smallcase",
  "Loans",
  "Financial Planning",
  "Wealth Management",
];

const socials = [
  { label: "LinkedIn", icon: Linkedin, slug: "linkedin", href: "https://www.linkedin.com/in/hr-kapital-7287b7433/" },
  { label: "Instagram", icon: Instagram, slug: "instagram", href: "https://www.instagram.com/hrkapital.finance/" },
];

export const Footer = () => (
  <footer data-testid="footer" className="relative border-t border-[#10202E]/[0.08] bg-[#E8EFF4]">
    <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
      <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <img
            src="/assets/logo-full.png"
            alt="HRKAPITAL"
            data-testid="footer-logo"
            className="h-16 w-auto"
          />
          <p className="mt-5 max-w-xs font-display text-xl italic text-[#4E6A82]">
            {BRAND.tagline}
          </p>
          <div className="mt-7 flex gap-3">
            {socials.map(({ label, icon: Icon, slug, href }) => (
              <a
                key={slug}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-link-${slug}`}
                aria-label={label}
                className="rounded-full border border-[#10202E]/15 bg-white/60 p-2.5 text-[#4E6A82] transition-[border-color,color] duration-300 hover:border-emerald-600/50 hover:text-emerald-700"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E8698]">Navigate</p>
          <ul className="mt-5 space-y-3">
            {navLinks.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  data-testid={`footer-nav-link-${l.id}`}
                  onClick={() => (l.href ? window.location.assign(l.href) : scrollToId(l.id))}
                  className="text-sm text-[#4E6A82] transition-colors duration-300 hover:text-emerald-700"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E8698]">Services</p>
          <ul className="mt-5 space-y-3">
            {serviceLinks.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => scrollToId("services")}
                  className="text-left text-sm text-[#4E6A82] transition-colors duration-300 hover:text-emerald-700"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E8698]">Contact</p>
          <ul className="mt-5 space-y-3 text-sm text-[#4E6A82]">
            <li>{BRAND.phone}</li>
            <li>{BRAND.email}</li>
            <li className="leading-relaxed">{BRAND.address}</li>
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#10202E]/[0.08] pt-8">
        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">
          <ShieldCheck className="h-3.5 w-3.5" />
          {BRAND.badge}
        </span>
        <span className="font-mono text-[11px] tracking-[0.15em] text-[#6E8698]">{BRAND.arn}</span>
        <span className="font-mono text-[11px] tracking-[0.15em] text-[#6E8698]">
          GSTIN {BRAND.gstin}
        </span>
      </div>

      <div
        data-testid="footer-disclaimer"
        className="mt-8 rounded-2xl border border-[#10202E]/10 bg-white/50 p-6"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6E8698]">Disclaimer</p>
        <p className="mt-3 max-w-4xl text-xs leading-relaxed text-[#4E6A82]">
          Mutual fund investments are subject to market risks. Read all scheme related documents
          carefully before investing. The information provided on this website is for general
          informational purposes only and should not be construed as investment advice or a
          guarantee of returns.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-[#8AA0B0]">© 2026 HRKAPITAL. All rights reserved.</p>
        <div className="flex flex-wrap gap-6">
          <a
            href="/disclosure"
            data-testid="footer-link-disclosure"
            className="text-xs text-[#8AA0B0] transition-colors duration-300 hover:text-[#33506A]"
          >
            Key Terms & Disclosure
          </a>
          <a
            href="/privacy"
            data-testid="footer-link-privacy"
            className="text-xs text-[#8AA0B0] transition-colors duration-300 hover:text-[#33506A]"
          >
            Privacy Policy
          </a>
          <a
            href="/terms"
            data-testid="footer-link-terms"
            className="text-xs text-[#8AA0B0] transition-colors duration-300 hover:text-[#33506A]"
          >
            Terms of Use
          </a>
        </div>
      </div>
    </div>
  </footer>
);
